import { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { useLanguage } from '../../i18n/LanguageContext';

interface UsernameAvailabilityProps {
  value: string;
  /** Skip the API call when value equals this current value (used by My Profile edit). */
  excludeCurrent?: string;
  onValidityChange?: (valid: boolean) => void;
}

type Status = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved';

export default function UsernameAvailability({ value, excludeCurrent, onValidityChange }: UsernameAvailabilityProps) {
  const { t } = useLanguage();
  const [status, setStatus] = useState<Status>('idle');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const trimmed = value.trim().toLowerCase();
    if (!trimmed) {
      setStatus('idle');
      onValidityChange?.(false);
      return;
    }
    if (excludeCurrent && trimmed === excludeCurrent.toLowerCase()) {
      // Editing My Profile and haven't actually changed the value yet — treat as neutral.
      setStatus('idle');
      onValidityChange?.(false);
      return;
    }

    setStatus('checking');
    const timer = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await api.checkUsernameAvailability(trimmed);
        if (ctrl.signal.aborted) return;
        if (res.available) {
          setStatus('available');
          onValidityChange?.(true);
        } else if (res.reason === 'INVALID_FORMAT') {
          setStatus('invalid');
          onValidityChange?.(false);
        } else if (res.reason === 'RESERVED') {
          setStatus('reserved');
          onValidityChange?.(false);
        } else {
          setStatus('taken');
          onValidityChange?.(false);
        }
      } catch {
        // Network error — be permissive; the server-side check at submit time is authoritative.
        setStatus('idle');
        onValidityChange?.(true);
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
    // onValidityChange intentionally omitted from deps — parent passes a new fn each render
    // and we don't want to re-fire the effect on every keystroke. The fn is called inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, excludeCurrent]);

  if (status === 'idle') return null;

  const message =
    status === 'checking' ? t('auth.username.checking') :
    status === 'available' ? t('auth.username.available') :
    status === 'taken' ? t('auth.username.taken') :
    status === 'reserved' ? t('auth.username.reserved') :
    t('auth.username.invalid');

  return (
    <div className={`username-availability username-availability--${status}`} aria-live="polite">
      {message}
    </div>
  );
}
